/**
 * Copper cleanup — the tidying a pour needs before it can be fabricated.
 *
 * Three operations, each matching what the original does rather than what the
 * names might suggest:
 *
 * - **Isolation islands** (`DI`). Copper that reaches no pad. `deleteisoislands`
 *   (OGmenu05.cpp:617) highlights everything electrically joined to any pad and
 *   then deletes every *line and arc on a copper layer* that was not reached.
 *   It is stranded track, not stranded fill.
 * - **Tear drops** (`BA` to add, `DR` to remove). A teardrop is a triangle at
 *   the junction of a track and a pad, widening the joint so a drill breaking
 *   out does not sever it. The original stores one as a three-vertex polyline
 *   on a copper layer, and `deleteteardrops` (OGmenu05.cpp:728) recognises them
 *   by exactly that: a copper polyline with three vertices.
 * - **Delete highlighted** (`DH`). Remove whatever Highlight Net last lit up.
 *
 * The geometry for creating a teardrop is taken from `blteardrop`
 * (OGmenu09.cpp:2328) so the shapes match the ones CIRCAD draws.
 */

import {
  LayerType, newId,
  type CadDocument, type ComponentEntity, type Entity, type FillEntity,
  type LineEntity, type PadEntity, type Vertex,
} from '../model.ts';
import { placeInComponent } from './netlist.ts';
import { padIsRectangular } from '../geom/pad.ts';

/** Layers that carry copper, by index. */
function copperLayers(doc: CadDocument): number[] {
  return doc.layers
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => l.type === LayerType.Copper || l.type === LayerType.Plane)
    .map(({ i }) => i);
}

/** The Pad Master, where a through pad reaches every copper layer. */
function padMasterIndex(doc: CadDocument): number {
  return doc.layers.findIndex((l) => l.name === 'Pad Master');
}

// ---------------------------------------------------------------------------
// Delete ▸ Isolation Islands
// ---------------------------------------------------------------------------

/**
 * Remove copper that connects to no pad.
 *
 * Connection is galvanic: two pieces are joined when they touch. Starting from
 * every pad, the connected copper is walked, and whatever is never reached is
 * an island — a track fragment left behind by editing, or a sliver a pour cut
 * loose. Returns the ids removed, so the caller can report and undo.
 */
export function deleteIsolationIslands(doc: CadDocument): number[] {
  if (doc.kind !== 'pcb') return [];
  const copper = copperLayers(doc);
  if (copper.length === 0) return [];

  const pieces = collectCopper(doc, copper);
  if (pieces.length === 0) return [];

  // Seed from the pads, then spread through everything that touches.
  const reached = new Set<number>();
  const queue: Piece[] = [];
  for (const p of pieces) {
    if (p.kind === 'pad') { reached.add(p.index); queue.push(p); }
  }
  if (queue.length === 0) return [];

  const grid = index(pieces);
  while (queue.length > 0) {
    const cur = queue.pop()!;
    for (const other of neighbours(grid, pieces, cur)) {
      if (reached.has(other.index)) continue;
      if (!sharesLayer(cur, other)) continue;
      if (!touches(cur, other)) continue;
      reached.add(other.index);
      queue.push(other);
    }
  }

  // Only lines and arcs are removed. A stranded *pad* is a part's pad and is
  // the placement's business, not a stray sliver — the original leaves them.
  //
  // An arc is several pieces, so an entity counts as connected when *any* of
  // its pieces was reached; requiring all of them would condemn a curve that
  // is joined at one end, which is every curved trace on the board.
  const anyReached = new Set<number>();
  const candidates = new Set<number>();
  for (const p of pieces) {
    if (p.kind === 'pad') { anyReached.add(p.id); continue; }
    candidates.add(p.id);
    if (reached.has(p.index)) anyReached.add(p.id);
  }
  const doomed = new Set<number>();
  for (const id of candidates) if (!anyReached.has(id)) doomed.add(id);
  if (doomed.size === 0) return [];

  doc.entities = doc.entities.filter((e) => !doomed.has(e.id));
  return [...doomed];
}

// ---------------------------------------------------------------------------
// Delete ▸ Tear Drops
// ---------------------------------------------------------------------------

/** Whether a fill is a teardrop: three vertices, on copper. */
function isTeardrop(e: Entity, copper: number[]): boolean {
  return e.kind === 'fill'
    && copper.includes(e.layer)
    && (e as FillEntity).vertices.length === 3;
}

/**
 * Remove every teardrop.
 *
 * The original's test is purely structural — a copper polyline with three
 * vertices — so a hand-drawn triangle on a copper layer goes too. That is the
 * behaviour, and matching it matters more than second-guessing it.
 */
export function deleteTeardrops(doc: CadDocument): number[] {
  if (doc.kind !== 'pcb') return [];
  const copper = copperLayers(doc);
  const doomed = doc.entities.filter((e) => isTeardrop(e, copper)).map((e) => e.id);
  if (doomed.length === 0) return [];
  const gone = new Set(doomed);
  doc.entities = doc.entities.filter((e) => !gone.has(e.id));
  return doomed;
}

/**
 * Add a teardrop wherever a track meets a pad.
 *
 * The triangle is the one `blteardrop` builds: apex 0.866 pad-widths out along
 * the track, base 0.25 out and 0.433 either side — an equilateral wedge from
 * the pad into the track. On an oblong or rectangular pad it is pushed out to
 * the pad's end, and a track that leaves at an angle the original does not
 * handle gets none.
 *
 * Returns the fills added.
 */
export function addTeardrops(doc: CadDocument): FillEntity[] {
  if (doc.kind !== 'pcb') return [];
  const copper = copperLayers(doc);
  if (copper.length === 0) return [];

  const pads = worldPads(doc);
  if (pads.length === 0) return [];

  // Existing teardrops, so running the command twice does not stack them.
  const existing = doc.entities.filter((e) => isTeardrop(e, copper)) as FillEntity[];
  const added: FillEntity[] = [];

  for (const pad of pads) {
    // The smaller dimension drives the shape; a hole wider than the pad has no
    // annulus to reinforce.
    const dy = Math.min(pad.sx, pad.sy);
    const dx = Math.max(pad.sx, pad.sy);
    if (dy <= 0 || (pad.hole ?? 0) > dy) continue;

    for (const line of doc.entities) {
      if (line.kind !== 'line') continue;
      if (!copper.includes(line.layer)) continue;
      const l = line as LineEntity;

      // One end must sit on the pad centre, within an eighth of the smaller of
      // the pad and the track.
      const tol = Math.min(dy, l.width) * 0.125;
      let x1: number, y1: number, x2: number, y2: number;
      if (Math.abs(l.x1 - pad.x) <= tol && Math.abs(l.y1 - pad.y) <= tol) {
        x1 = l.x1; y1 = l.y1; x2 = l.x2; y2 = l.y2;
      } else if (Math.abs(l.x2 - pad.x) <= tol && Math.abs(l.y2 - pad.y) <= tol) {
        x1 = l.x2; y1 = l.y2; x2 = l.x1; y2 = l.y1;
      } else continue;

      const angle = Math.atan2(y2 - y1, x2 - x1);
      const base = dy * 0.433;
      const apex = dy * 0.866;
      let offset = 0;

      // An oblong or rectangular pad only takes a teardrop when the track
      // leaves along one of its axes; between those it is left alone.
      if (dx !== dy || pad.rect) {
        // Incident angle in turns, as the original measures it.
        const turns = (angle / (Math.PI * 2)) - pad.rot / 360 + 0.0078125;
        let za = turns % 1;
        if (za < 0) za += 1;
        if (za >= 0.5) za -= 0.5;
        if (za < 0.015625) offset = (dx - dy) * 0.5;
        else if (za < 0.25) continue;
        else if (za >= 0.265625) continue;
      }
      if (pad.rect) offset += dy * 0.125;

      // A track shorter than the teardrop would be swallowed by it.
      if (Math.hypot(x2 - x1, y2 - y1) + l.width * 0.5 < apex + offset) continue;

      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const put = (lx: number, ly: number): Vertex => ({
        x: pad.x + lx * cos - ly * sin,
        y: pad.y + lx * sin + ly * cos,
      });
      const vertices = [
        put(apex + offset, 0),
        put(dy * 0.25 + offset, base),
        put(dy * 0.25 + offset, -base),
      ];

      if (alreadyThere(existing, added, vertices)) continue;

      const xs = vertices.map((v) => v.x);
      const ys = vertices.map((v) => v.y);
      const drop: FillEntity = {
        kind: 'fill', id: newId(), layer: line.layer,
        // Zero width is what makes it solid copper rather than an outline.
        width: 0,
        bounds: {
          x1: Math.min(...xs), y1: Math.min(...ys),
          x2: Math.max(...xs), y2: Math.max(...ys),
        },
        vertices,
        ...(l.signal ? { signal: l.signal } : {}),
      };
      added.push(drop);
    }
  }

  doc.entities.push(...added);
  return added;
}

/** Whether a triangle with these corners is already present. */
function alreadyThere(
  existing: FillEntity[], added: FillEntity[], vertices: Vertex[],
): boolean {
  const EPS = 1e-6;
  const same = (f: FillEntity): boolean =>
    f.vertices.length === 3
    && vertices.every((v) => f.vertices.some(
      (w) => Math.abs(w.x - v.x) < EPS && Math.abs(w.y - v.y) < EPS));
  return existing.some(same) || added.some(same);
}

// ---------------------------------------------------------------------------
// Delete ▸ Highlighted Items
// ---------------------------------------------------------------------------

/**
 * Remove the highlighted set.
 *
 * Only top-level entities go: a pad inside a placed part belongs to the part,
 * and deleting it from under the component would leave the footprint wrong
 * rather than the board tidier.
 */
export function deleteHighlighted(doc: CadDocument, ids: ReadonlySet<number>): number[] {
  if (ids.size === 0) return [];
  const removed = doc.entities.filter((e) => ids.has(e.id)).map((e) => e.id);
  if (removed.length === 0) return [];
  const gone = new Set(removed);
  doc.entities = doc.entities.filter((e) => !gone.has(e.id));
  return removed;
}

// ---------------------------------------------------------------------------
// Connectivity support
// ---------------------------------------------------------------------------

interface Piece {
  index: number;
  id: number;
  kind: 'line' | 'arc' | 'pad';
  layers: number[];
  /** Endpoints (a pad and an arc use their extremes). */
  x1: number; y1: number; x2: number; y2: number;
  half: number;
  bx1: number; by1: number; bx2: number; by2: number;
}

interface WorldPad {
  x: number; y: number;
  sx: number; sy: number;
  rot: number;
  rect: boolean;
  hole?: number;
}

/** Every pad in world coordinates, components included. */
function worldPads(doc: CadDocument): WorldPad[] {
  const out: WorldPad[] = [];
  const walk = (list: Entity[], owner: ComponentEntity | null): void => {
    for (const e of list) {
      if (e.kind === 'component') { walk(e.children, e); continue; }
      if (e.kind !== 'pad') continue;
      const p = e as PadEntity;
      if (p.type === 'T0' || p.type === 'S3') continue;
      const w = owner ? placeInComponent({ x: p.x, y: p.y }, owner) : { x: p.x, y: p.y };
      out.push({
        x: w.x, y: w.y, sx: p.sizeX, sy: p.sizeY,
        rot: (p.rotation ?? 0) + (owner?.rotation ?? 0),
        rect: padIsRectangular(p),
        ...(p.hole ? { hole: p.hole } : {}),
      });
    }
  };
  walk(doc.entities, null);
  return out;
}

/** Copper primitives, flattened to world space. */
function collectCopper(doc: CadDocument, copper: number[]): Piece[] {
  const out: Piece[] = [];
  const master = padMasterIndex(doc);

  /**
   * The copper layers a primitive occupies, or null if it is not copper.
   *
   * The Pad Master promotion applies to **pads only**. A through pad drawn
   * there is present on every copper layer, but a *line* on the Pad Master is
   * the board outline — CIRCAD's convention, and what {@link outlineEntities}
   * reads. Promoting those too made the outline look like stranded copper
   * connected to no pad, and the island sweep deleted it: on 5300quoteB that
   * was all four sides of the board. The original avoids this by testing the
   * layer's *type*, and Pad Master is a silk-type layer, not copper.
   */
  const layersFor = (layer: number, isPad: boolean): number[] | null => {
    if (isPad && (layer === master || copper.includes(layer))) return copper;
    return copper.includes(layer) ? [layer] : null;
  };

  const push = (p: Omit<Piece, 'index'>): void => {
    out.push({ ...p, index: out.length });
  };

  const walk = (list: Entity[], owner: ComponentEntity | null): void => {
    for (const e of list) {
      if (e.kind === 'component') { walk(e.children, e); continue; }

      if (e.kind === 'line') {
        const layers = layersFor(e.layer, false);
        if (!layers) continue;
        const a = owner ? placeInComponent({ x: e.x1, y: e.y1 }, owner) : { x: e.x1, y: e.y1 };
        const b = owner ? placeInComponent({ x: e.x2, y: e.y2 }, owner) : { x: e.x2, y: e.y2 };
        const half = e.width / 2;
        push({
          id: e.id, kind: 'line', layers,
          x1: a.x, y1: a.y, x2: b.x, y2: b.y, half,
          bx1: Math.min(a.x, b.x) - half, by1: Math.min(a.y, b.y) - half,
          bx2: Math.max(a.x, b.x) + half, by2: Math.max(a.y, b.y) + half,
        });
        continue;
      }

      if (e.kind === 'arc') {
        const layers = layersFor(e.layer, false);
        if (!layers) continue;
        const at = (deg: number): Vertex => {
          const r = (deg * Math.PI) / 180;
          const p = { x: e.cx + e.radius * Math.cos(r), y: e.cy + e.radius * Math.sin(r) };
          return owner ? placeInComponent(p, owner) : p;
        };
        // An arc is walked as a chain of short chords rather than one chord
        // end to end. A curved trace is not its chord: taking the straight
        // line between the ends misses everything that joins the curve part
        // way along, and the island sweep then reads the whole run as
        // stranded — 174 of 851 traces on one board, all of them real.
        const steps = Math.max(2, Math.ceil(Math.abs(e.sweep) / 15));
        const half = e.width / 2;
        let prev = at(e.angle);
        for (let s = 1; s <= steps; s++) {
          const next = at(e.angle + (e.sweep * s) / steps);
          push({
            id: e.id, kind: 'arc', layers,
            x1: prev.x, y1: prev.y, x2: next.x, y2: next.y, half,
            bx1: Math.min(prev.x, next.x) - half, by1: Math.min(prev.y, next.y) - half,
            bx2: Math.max(prev.x, next.x) + half, by2: Math.max(prev.y, next.y) + half,
          });
          prev = next;
        }
        continue;
      }

      if (e.kind === 'pad') {
        const p = e as PadEntity;
        if (p.type === 'T0' || p.type === 'S3') continue;
        const layers = layersFor(p.layer, true);
        if (!layers) continue;
        const w = owner ? placeInComponent({ x: p.x, y: p.y }, owner) : { x: p.x, y: p.y };
        const half = Math.min(p.sizeX, p.sizeY) / 2;
        push({
          id: p.id, kind: 'pad', layers,
          x1: w.x, y1: w.y, x2: w.x, y2: w.y, half,
          bx1: w.x - p.sizeX / 2, by1: w.y - p.sizeY / 2,
          bx2: w.x + p.sizeX / 2, by2: w.y + p.sizeY / 2,
        });
      }
    }
  };

  walk(doc.entities, null);
  return out;
}

const CELL = 0.2;

/** Bucket pieces by cell, so the spread only looks at what is nearby. */
function index(pieces: Piece[]): Map<string, number[]> {
  const grid = new Map<string, number[]>();
  for (const p of pieces) {
    for (let cx = Math.floor(p.bx1 / CELL); cx <= Math.floor(p.bx2 / CELL); cx++) {
      for (let cy = Math.floor(p.by1 / CELL); cy <= Math.floor(p.by2 / CELL); cy++) {
        const k = `${cx},${cy}`;
        const list = grid.get(k);
        if (list) list.push(p.index);
        else grid.set(k, [p.index]);
      }
    }
  }
  return grid;
}

function neighbours(grid: Map<string, number[]>, pieces: Piece[], of: Piece): Piece[] {
  const out: Piece[] = [];
  const seen = new Set<number>();
  for (let cx = Math.floor(of.bx1 / CELL); cx <= Math.floor(of.bx2 / CELL); cx++) {
    for (let cy = Math.floor(of.by1 / CELL); cy <= Math.floor(of.by2 / CELL); cy++) {
      for (const i of grid.get(`${cx},${cy}`) ?? []) {
        if (i === of.index || seen.has(i)) continue;
        seen.add(i);
        out.push(pieces[i]!);
      }
    }
  }
  return out;
}

function sharesLayer(a: Piece, b: Piece): boolean {
  for (const l of a.layers) if (b.layers.includes(l)) return true;
  return false;
}

/** Copper touches when the gap between the two closes to nothing. */
function touches(a: Piece, b: Piece): boolean {
  const d = segSeg(a.x1, a.y1, a.x2, a.y2, b.x1, b.y1, b.x2, b.y2);
  return d - a.half - b.half <= 1e-9;
}

function pointSeg(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len = dx * dx + dy * dy;
  if (len === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function segSeg(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): number {
  const d1 = cross(cx, cy, dx, dy, ax, ay);
  const d2 = cross(cx, cy, dx, dy, bx, by);
  const d3 = cross(ax, ay, bx, by, cx, cy);
  const d4 = cross(ax, ay, bx, by, dx, dy);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0))
    && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return 0;
  return Math.min(
    pointSeg(ax, ay, cx, cy, dx, dy),
    pointSeg(bx, by, cx, cy, dx, dy),
    pointSeg(cx, cy, ax, ay, bx, by),
    pointSeg(dx, dy, ax, ay, bx, by),
  );
}

function cross(ax: number, ay: number, bx: number, by: number, px: number, py: number): number {
  return (bx - ax) * (py - ay) - (by - ay) * (px - ax);
}
